import React, { useState, useEffect, useCallback } from "react";
import { useAuth } from "../context/AuthContext";
import { getSocket } from "../utils/socket";
import Navbar from "../components/Navbar";
import ChatWidgetParticipants from "../components/ChatWidgetParticipants";
import ProfileStats from "../components/ProfileStats";
import UserSearch from "../components/UserSearch";
import UserList from "../components/UserList";
import SendGift from "./SendGift";

const FriendsPage = () => {
  const { user: authUser, token, fetchWithRetry } = useAuth();
  const [friends, setFriends] = useState([]);
  const [friendRequests, setFriendRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isLoadingFriend, setIsLoadingFriend] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [sortBy, setSortBy] = useState("last_activity");
  const [showPhotoGallery, setShowPhotoGallery] = useState(null);
  const [chatUsers, setChatUsers] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalUsers, setTotalUsers] = useState(0);
  const [activeTab, setActiveTab] = useState("friends");
  const itemsPerPage = 18;

  const [genderFilter, setGenderFilter] = useState("");
  const [minAgeFilter, setMinAgeFilter] = useState("");
  const [maxAgeFilter, setMaxAgeFilter] = useState("");
  const [countryFilter, setCountryFilter] = useState("");
  const [stateFilter, setStateFilter] = useState("");
  const [cityFilter, setCityFilter] = useState("");

  const [coinBalance, setCoinBalance] = useState(0);
  const [showGiftModal, setShowGiftModal] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [tooltip, setTooltip] = useState(null);
  const [isTooltipFading, setIsTooltipFading] = useState(false);

  const showTooltip = useCallback((message, type = "success") => {
    setTooltip({ message, type });
    setTimeout(() => {
      setIsTooltipFading(true);
      setTimeout(() => {
        setTooltip(null);
        setIsTooltipFading(false);
      }, 300);
    }, 1500);
  }, []);

  const fetchFriends = useCallback(
    async (page = 1) => {
      try {
        setLoading(true);
        setError(null);
        const query = new URLSearchParams({
          page: page,
          limit: itemsPerPage,
          search: searchQuery,
          sort: sortBy,
          gender: genderFilter,
          minAge: minAgeFilter,
          maxAge: maxAgeFilter,
          country: countryFilter,
          state: stateFilter,
          city: cityFilter,
        }).toString();
        const res = await fetchWithRetry({
          method: "get",
          url: `/api/users/friends?${query}`,
        });
        const fetchedFriends = res.data.friends || [];
        console.log("Dữ liệu bạn bè thô từ API:", fetchedFriends);
        const validFriends = fetchedFriends.filter((friend) => {
          if (!friend._id) {
            console.warn(`Bạn bè thiếu _id:`, friend);
            return false;
          }
          if (!friend.membershipId) {
            console.warn(`Bạn bè ${friend._id} thiếu membershipId`);
            return false;
          }
          return true;
        });
        setFriends(validFriends.map((friend) => ({
          ...friend,
          primaryPhoto: friend.primaryPhoto || { photoUrl: "", caption: "" },
          mainImage: friend.mainImage || "",
          avatar: friend.avatar || "",
        })));
        setTotalPages(res.data.totalPages || 1);
        setTotalUsers(res.data.total || validFriends.length);
        setCurrentPage(page);
        if (validFriends.length === 0) {
          setError("Bạn chưa có bạn bè nào hoặc dữ liệu không hợp lệ.");
        }
      } catch (error) {
        console.error("Lỗi khi lấy danh sách bạn bè:", error.response || error);
        setError(
          error.response?.status === 401
            ? "Phiên đăng nhập hết hạn. Vui lòng đăng nhập lại!"
            : error.response?.data?.message || "Không thể tải danh sách bạn bè."
        );
        setFriends([]);
      } finally {
        setLoading(false);
      }
    },
    [
      searchQuery,
      sortBy,
      genderFilter,
      minAgeFilter,
      maxAgeFilter,
      countryFilter,
      stateFilter,
      cityFilter,
      fetchWithRetry,
    ]
  );

  const fetchFriendRequests = useCallback(
    async () => {
      try {
        setLoading(true);
        setError(null);
        const res = await fetchWithRetry({
          method: "get",
          url: "/api/users/friend-requests",
        });

        const receivedRequests = res.data.requests || [];
        console.log("Dữ liệu yêu cầu kết bạn thô từ API:", receivedRequests);

        const validRequests = receivedRequests.filter((req) => {
          if (!req.senderId || !req.senderId._id || !req.senderId.name) {
            console.warn(`Yêu cầu ${req._id} có senderId không hợp lệ:`, req.senderId);
            return false;
          }
          if (!req.receiverId || !req.receiverId._id) {
            console.warn(`Yêu cầu ${req._id} có receiverId không hợp lệ:`, req.receiverId);
            return false;
          }
          if (!req.senderId.membershipId) {
            console.warn(`Sender ${req.senderId._id} thiếu membershipId`);
            return false;
          }
          return String(req.receiverId._id || req.receiverId) === String(authUser._id);
        });

        setFriendRequests(validRequests.map((req) => ({
          ...req,
          senderId: {
            ...req.senderId,
            primaryPhoto: req.senderId.primaryPhoto || { photoUrl: "", caption: "" },
            mainImage: req.senderId.mainImage || "",
            avatar: req.senderId.avatar || "",
            membershipId: req.senderId.membershipId,
          },
        })));
        if (validRequests.length === 0) {
          setError("Không có yêu cầu kết bạn nào đang chờ chấp nhận.");
        }
      } catch (error) {
        console.error("Lỗi khi lấy danh sách yêu cầu kết bạn:", error.response || error);
        setError(
          error.response?.status === 401
            ? "Phiên đăng nhập hết hạn. Vui lòng đăng nhập lại!"
            : error.response?.data?.message || "Không thể tải danh sách yêu cầu kết bạn."
        );
        setFriendRequests([]);
      } finally {
        setLoading(false);
      }
    },
    [fetchWithRetry, authUser]
  );

  const fetchNotifications = useCallback(
    async () => {
      try {
        const res = await fetchWithRetry({
          method: "get",
          url: "/api/users/notifications",
        });
        setNotifications(res.data);
        const unread = res.data.filter((n) => !n.read).length;
        setUnreadCount(unread);
      } catch (error) {
        console.error("Lỗi khi lấy thông báo:", error.response ? error.response.data : error.message);
        setError(
          error.response?.status === 401
            ? "Phiên đăng nhập hết hạn. Vui lòng đăng nhập lại!"
            : error.response?.data?.message || "Không thể tải thông báo."
        );
      }
    },
    [fetchWithRetry]
  );

  useEffect(() => {
    if (authUser) {
      const fetchData = async () => {
        if (activeTab === "friends") {
          await fetchFriends(currentPage);
        } else {
          await fetchFriendRequests();
        }
        await fetchNotifications();
      };

      fetchData();

      const pollInterval = setInterval(fetchNotifications, 10000);

      const socket = getSocket();
      if (!socket.connected) {
        socket.connect();
      }

      socket.on("connect", () => {
        console.log("Kết nối WebSocket thành công:", socket.id);
        socket.emit("joinRoom", authUser._id);
      });

      socket.on("notification", (notification) => {
        setNotifications((prev) => [notification, ...prev]);
        setUnreadCount((prev) => prev + 1);
        if (
          notification.type === "friend_request" ||
          notification.type === "new_friend"
        ) {
          if (activeTab === "requests") {
            fetchFriendRequests();
          } else {
            fetchFriends(currentPage);
          }
        }
      });

      socket.on("friendListUpdate", () => {
        console.log("Nhận sự kiện cập nhật danh sách bạn bè");
        fetchFriends(currentPage);
      });

      socket.on("coinBalanceUpdate", (data) => {
        setCoinBalance(data.coinBalance);
        showTooltip(`Số dư coin của bạn đã được cập nhật: ${data.coinBalance} coin`, "success");
      });

      return () => {
        clearInterval(pollInterval);
        socket.off("connect");
        socket.off("notification");
        socket.off("friendListUpdate");
        socket.off("coinBalanceUpdate");
      };
    }
  }, [
    authUser,
    token,
    activeTab,
    currentPage,
    fetchFriends,
    fetchFriendRequests,
    fetchNotifications,
    showTooltip,
  ]);

  const handleRemoveFriend = async (friendId) => {
    if (isLoadingFriend) return;
    setIsLoadingFriend(true);
    try {
      if (!authUser || !authUser._id) {
        throw new Error("Người dùng chưa được tải, vui lòng thử lại!");
      }
      const response = await fetchWithRetry({
        method: "put",
        url: "/api/users/remove-friend",
        data: { friendId },
      });
      showTooltip(response.data.message || "Xóa bạn bè thành công!", "success");
      await fetchFriends(currentPage);
      await fetchNotifications();
    } catch (error) {
      console.error("Lỗi khi xóa bạn bè:", error.response?.data || error.message);
      showTooltip(
        error.response?.status === 401
          ? "Phiên đăng nhập hết hạn. Vui lòng đăng nhập lại!"
          : error.response?.data?.message || error.message || "Lỗi khi xóa bạn bè",
        "error"
      );
    } finally {
      setIsLoadingFriend(false);
    }
  };

  const handleRespondFriendRequest = async (requestId, action) => {
    if (isLoadingFriend) return;
    setIsLoadingFriend(true);
    try {
      if (!authUser || !authUser._id) {
        throw new Error("Người dùng chưa được tải, vui lòng thử lại!");
      }

      const request = friendRequests.find((req) => req._id === requestId);
      if (!request) {
        throw new Error("Yêu cầu kết bạn không tồn tại!");
      }

      if (String(request.receiverId._id || request.receiverId) !== String(authUser._id)) {
        throw new Error("Bạn không có quyền xử lý yêu cầu này!");
      }

      const response = await fetchWithRetry({
        method: "put",
        url: `/api/users/friend-request/${requestId}`,
        data: { action },
      });

      showTooltip(
        response.data.message ||
          (action === "accept"
            ? "Chấp nhận yêu cầu kết bạn thành công!"
            : "Từ chối yêu cầu kết bạn thành công!"),
        "success"
      );

      setFriendRequests((prev) => prev.filter((req) => req._id !== requestId));
      if (action === "accept") {
        await fetchFriends(currentPage);
      }
      await fetchNotifications();
    } catch (error) {
      console.error("Lỗi chi tiết khi phản hồi Friend Request:", error.response?.data || error);
      showTooltip(
        error.response?.status === 401
          ? "Phiên đăng nhập hết hạn. Vui lòng đăng nhập lại!"
          : error.response?.status === 403
          ? "Bạn không có quyền xử lý yêu cầu này!"
          : error.response?.data?.message || error.message || "Lỗi khi phản hồi yêu cầu kết bạn",
        "error"
      );
    } finally {
      setIsLoadingFriend(false);
    }
  };

  const handleToggleNotifications = async (notificationId, action) => {
    if (!token) {
      showTooltip("Không có token, vui lòng đăng nhập lại!", "error");
      return;
    }

    if (!action || !notificationId) {
      return;
    }

    try {
      let response;
      if (action === "read") {
        response = await fetchWithRetry({
          method: "put",
          url: `/api/users/notifications/${notificationId}/read`,
          data: {},
        });
      } else if (action === "delete") {
        response = await fetchWithRetry({
          method: "delete",
          url: `/api/users/notifications/${notificationId}`,
        });
      } else {
        console.warn(`Hành động không hợp lệ: ${action}`);
        return;
      }

      if (response.status === 200) {
        if (action === "read") {
          setNotifications((prev) =>
            prev.map((n) => (n._id === notificationId ? { ...n, read: true } : n))
          );
          setUnreadCount((prev) => prev - 1);
        } else if (action === "delete") {
          setNotifications((prev) => prev.filter((n) => n._id !== notificationId));
          setUnreadCount((prev) => {
            const deletedNotification = notifications.find((n) => n._id === notificationId);
            return deletedNotification && !deletedNotification.read ? prev - 1 : prev;
          });
        }
      }
    } catch (error) {
      console.error(
        `Lỗi khi ${action} thông báo:`,
        error.response ? error.response.data : error.message
      );
      showTooltip(
        error.response?.status === 401
          ? "Phiên đăng nhập hết hạn. Vui lòng đăng nhập lại!"
          : error.response?.data?.message || `Lỗi khi ${action} thông báo!`,
        "error"
      );
    }
  };

  const handleSearch = () => {
    if (activeTab === "friends") {
      setCurrentPage(1);
      fetchFriends(1);
    }
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    fetchFriends(page);
  };

  const handleSendGift = (userId) => {
    const selectedUser = friends.find((user) => user._id === userId);
    if (selectedUser) {
      if (!selectedUser._id || !selectedUser.membershipId) {
        console.warn(`Người dùng ${userId} thiếu _id hoặc membershipId:`, selectedUser);
        showTooltip("Không thể gửi quà: Dữ liệu người dùng không đầy đủ!", "error");
        return;
      }
      setSelectedUserId({
        membershipId: selectedUser.membershipId,
        id: selectedUser._id,
      });
      setShowGiftModal(true);
    } else {
      showTooltip("Không tìm thấy người dùng!", "error");
    }
  };

  const handleGiftSent = useCallback(
    async (gift) => {
      if (!selectedUserId?.id) {
        showTooltip("Không thể gửi quà: Người nhận không hợp lệ!", "error");
        return;
      }
      if (!gift || !gift._id) {
        showTooltip("Không thể gửi quà: Quà tặng không hợp lệ!", "error");
        return;
      }

      try {
        const response = await fetchWithRetry({
          method: "post",
          url: "/api/users/send-gift",
          data: { receiverId: selectedUserId.id, giftId: gift._id },
        });

        if (response.status === 200) {
          setShowGiftModal(false);
          setCoinBalance(response.data.newCoinBalance || coinBalance);
          showTooltip(`Đã gửi quà "${gift.name}" thành công!`, "success");
          await fetchNotifications();
        } else {
          throw new Error("Phản hồi từ server không hợp lệ!");
        }
      } catch (error) {
        console.error("Lỗi khi gửi quà tặng:", error);
        showTooltip(
          error.response?.status === 401
            ? "Phiên đăng nhập hết hạn. Vui lòng đăng nhập lại!"
            : error.response?.data?.message || "Lỗi khi gửi quà tặng!",
          "error"
        );
      }
    },
    [fetchWithRetry, selectedUserId, showTooltip, fetchNotifications, coinBalance]
  );

  const handleViewPhotos = (user) => {
    if (!user._id || !user.membershipId) {
      console.warn(`Người dùng thiếu _id hoặc membershipId:`, user);
      showTooltip("Không thể xem ảnh: Dữ liệu người dùng không đầy đủ!", "error");
      return;
    }
    if (!user.photos || user.photos.length === 0) {
      showTooltip("Người dùng này chưa có ảnh nào để hiển thị!", "info");
      return;
    }
    setShowPhotoGallery(user);
  };

  const handleOpenChat = (userId) => {
    if (!userId || !authUser || !authUser._id) {
      showTooltip("Không thể mở chat: Dữ liệu người dùng không hợp lệ!", "error");
      console.error("Dữ liệu người dùng không hợp lệ", { userId, authUser });
      return;
    }
    const selectedUser = friends.find((u) => u._id === userId);
    if (!selectedUser) {
      showTooltip("Người dùng không tồn tại trong danh sách bạn bè!", "error");
      console.error(`Không tìm thấy người dùng với _id ${userId} trong danh sách bạn bè`);
      return;
    }
    if (!selectedUser._id || !selectedUser.membershipId) {
      showTooltip("Dữ liệu người dùng không đầy đủ: Thiếu ID hoặc membershipId!", "error");
      console.error(`Dữ liệu người dùng không hợp lệ cho _id ${userId}`, selectedUser);
      return;
    }
    setChatUsers((prev) => [...new Set([...prev, selectedUser._id])]);
  };

  if (!authUser) {
    return <p className="text-center text-gray-600">Đang tải...</p>;
  }

  const displayedFriendRequests = friendRequests.map((req) => {
    if (!req.senderId._id) {
      console.warn(`Yêu cầu kết bạn ${req._id} thiếu sender _id:`, req.senderId);
      return null;
    }
    return {
      ...req.senderId,
      primaryPhoto: req.senderId.primaryPhoto || { photoUrl: "", caption: "" },
      mainImage: req.senderId.mainImage || "",
      avatar: req.senderId.avatar || "",
      requestId: req._id,
      isFriendRequestPending: true,
      isSentByCurrentUser: false,
    };
  }).filter((req) => req !== null);

  return (
    <div className="font-sans min-h-screen bg-gray-100">
      <Navbar
        unreadCount={unreadCount}
        unreadNotificationCount={unreadCount}
        notifications={notifications}
        onToggleNotifications={handleToggleNotifications}
      />

      <div className="pt-20 max-w-[1600px] mx-auto p-8">
        <ProfileStats authUser={authUser} coinBalance={coinBalance} />

        <div className="mb-4">
          <p className="text-gray-700">
            Bạn có{" "}
            <span className="font-semibold">{friendRequests.length}</span>{" "}
            yêu cầu kết bạn đang chờ chấp nhận.
          </p>
        </div>

        <div className="flex border-b mb-6">
          <button
            onClick={() => {
              setActiveTab("friends");
              setCurrentPage(1);
              fetchFriends(1);
            }}
            className={`px-4 py-2 font-medium ${
              activeTab === "friends"
                ? "border-b-2 border-blue-500 text-blue-600"
                : "text-gray-600 hover:text-blue-600"
            }`}
          >
            Bạn bè ({friends.length})
          </button>
          <button
            onClick={() => {
              setActiveTab("requests");
              fetchFriendRequests();
            }}
            className={`px-4 py-2 font-medium ${
              activeTab === "requests"
                ? "border-b-2 border-blue-500 text-blue-600"
                : "text-gray-600 hover:text-blue-600"
            }`}
          >
            Yêu cầu kết bạn ({friendRequests.length})
          </button>
        </div>

        {activeTab === "friends" && (
          <UserSearch
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
            genderFilter={genderFilter}
            setGenderFilter={setGenderFilter}
            minAgeFilter={minAgeFilter}
            setMinAgeFilter={setMinAgeFilter}
            maxAgeFilter={maxAgeFilter}
            setMaxAgeFilter={setMaxAgeFilter}
            countryFilter={countryFilter}
            setCountryFilter={setCountryFilter}
            stateFilter={stateFilter}
            setStateFilter={setStateFilter}
            cityFilter={cityFilter}
            setCityFilter={setCityFilter}
            sortBy={sortBy}
            setSortBy={setSortBy}
            handleSearch={handleSearch}
          />
        )}

        {loading ? (
          <p className="text-center text-gray-500">
            Đang tải danh sách {activeTab === "friends" ? "bạn bè" : "yêu cầu kết bạn"}...
          </p>
        ) : error ? (
          <p className="text-red-500 text-center mb-4">{error}</p>
        ) : activeTab === "friends" ? (
          <UserList
            filteredUsers={friends}
            authUser={authUser}
            likeStates={{}}
            friendRequestStates={{}}
            friendRequests={[]}
            matches={[]}
            friends={friends}
            isLoadingLike={false}
            isLoadingFriend={isLoadingFriend}
            handleLike={() => {}}
            handleUnlike={() => {}}
            sendFriendRequest={() => {}}
            cancelFriendRequest={() => {}}
            handleRespondFriendRequest={handleRespondFriendRequest}
            handleOpenChat={handleOpenChat}
            handleSendGift={handleSendGift}
            handleViewPhotos={handleViewPhotos}
            handleRemoveFriend={handleRemoveFriend}
            currentPage={currentPage}
            totalPages={totalPages}
            totalUsers={totalUsers}
            handlePageChange={handlePageChange}
            showTooltip={showTooltip}
            loading={loading}
            skeletonCount={18}
            isFriendList={true}
          />
        ) : (
          <UserList
            filteredUsers={displayedFriendRequests}
            authUser={authUser}
            likeStates={{}}
            friendRequestStates={displayedFriendRequests.reduce((acc, req) => {
              acc[req._id] = true;
              return acc;
            }, {})}
            friendRequests={friendRequests}
            matches={[]}
            friends={friends}
            isLoadingLike={false}
            isLoadingFriend={isLoadingFriend}
            handleLike={() => {}}
            handleUnlike={() => {}}
            sendFriendRequest={() => {}}
            cancelFriendRequest={() => {}}
            handleRespondFriendRequest={handleRespondFriendRequest}
            handleOpenChat={handleOpenChat}
            handleSendGift={handleSendGift}
            handleViewPhotos={handleViewPhotos}
            currentPage={1}
            totalPages={1}
            totalUsers={friendRequests.length}
            handlePageChange={() => {}}
            showTooltip={showTooltip}
            loading={loading}
            skeletonCount={18}
            isFriendRequestList={true}
          />
        )}

        {showGiftModal && selectedUserId && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 max-w-3xl w-full max-h-[80vh] overflow-y-auto">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-semibold">Chọn Quà Tặng</h2>
                <button
                  onClick={() => setShowGiftModal(false)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  X
                </button>
              </div>
              <SendGift
                selectedUserId={selectedUserId}
                onClose={() => setShowGiftModal(false)}
                onGiftSent={handleGiftSent}
              />
            </div>
          </div>
        )}

        {showPhotoGallery && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 max-w-3xl w-full max-h-[80vh] overflow-y-auto">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-semibold">
                  Thư viện ảnh của {showPhotoGallery.name}
                </h2>
                <button
                  onClick={() => setShowPhotoGallery(null)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  X
                </button>
              </div>
              {showPhotoGallery.photos && showPhotoGallery.photos.length > 0 ? (
                <div className="grid grid-cols-3 gap-4">
                  {showPhotoGallery.photos.map((photo, index) => (
                    <div key={index} className="relative">
                      <img
                        src={photo.photoUrl}
                        alt={`Gallery item ${index + 1} for ${showPhotoGallery.name}`}
                        className="w-full h-40 object-cover rounded-lg"
                        onError={(e) => (e.target.src = "https://placehold.co/150x150")}
                      />
                      {photo.caption && (
                        <p className="text-sm text-gray-600 mt-1">{photo.caption}</p>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-center text-gray-500">Không có ảnh nào để hiển thị.</p>
              )}
            </div>
          </div>
        )}

        {tooltip && (
          <div
            className={`fixed top-4 right-4 text-white px-4 py-2 rounded-lg shadow-lg z-50 flex items-center space-x-2 ${
              isTooltipFading ? "animate-fade-out" : "animate-slide-in"
            } ${tooltip.type === "success" ? "bg-green-500" : tooltip.type === "info" ? "bg-blue-500" : "bg-red-500"}`}
          >
            <span>{tooltip.message}</span>
          </div>
        )}

        {chatUsers.length > 0 && (
          <ChatWidgetParticipants
            onSelectChat={chatUsers[chatUsers.length - 1]}
            onClose={() => setChatUsers([])}
            users={[]}
            friendsList={friends}
          />
        )}
      </div>
    </div>
  );
};

export default FriendsPage;